import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { DocumentMathTextBlock } from '../components/MathTextBlock';
import { KnowledgeParticleQaSections } from '../components/KnowledgeParticleQaSections';
import { TtqibEmbeddedImage } from '../features/ttqib/TtqibEmbeddedImage';
import { readEmbeddedImageRecord } from '../features/ttqib/embeddedImageUtils';
import {
  factoryService,
  type FactoryOrderListFilter,
  type FactoryReleasePreviewDraft,
} from '../services/factoryService';
import type { KnowledgeParticle } from '../types/domain';

type LocationState = {
  filter?: FactoryOrderListFilter;
};

function draftToParticle(draft: FactoryReleasePreviewDraft): KnowledgeParticle {
  return {
    id: draft.id,
    user_id: '',
    created_at: '',
    updated_at: '',
    title: draft.title,
    questions: draft.questions as unknown as KnowledgeParticle['questions'],
    answers: draft.answers as unknown as KnowledgeParticle['answers'],
    metadata: draft.metadata,
  };
}

export function LearningFactoryOrderParticlePage() {
  const { orderId = '', draftId = '' } = useParams<{ orderId: string; draftId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const returnFilter = ((location.state as LocationState | null)?.filter) || 'done';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<FactoryReleasePreviewDraft | null>(null);
  const [implicitCapabilities, setImplicitCapabilities] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!orderId || !draftId) return;
      try {
        setLoading(true);
        setError(null);
        const preview = await factoryService.getOrderReleasePreview(orderId);
        if (cancelled) return;
        const decoded = decodeURIComponent(draftId);
        const product = preview.products.find((p) => p.bankDrafts.some((d) => d.id === decoded));
        const found = product?.bankDrafts.find((d) => d.id === decoded);
        if (!found) throw new Error('未找到该知识颗粒');
        setDraft(found);
        setImplicitCapabilities(product?.implicitCapabilities || []);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [draftId, orderId]);

  const embeddedImage = useMemo(
    () => readEmbeddedImageRecord(draft?.metadata || null),
    [draft?.metadata],
  );

  return (
    <div className="mx-auto max-w-3xl space-y-5 px-4 py-6">
      <header className="space-y-2">
        <button
          type="button"
          onClick={() => navigate(`/factory-orders/${orderId}`, {
            state: { filter: returnFilter, focusDraftId: decodeURIComponent(draftId) },
          })}
          className="text-sm font-medium text-primary"
        >
          ← 返回颗粒列表
        </button>
        <h1 className="text-xl font-semibold text-ink">释放颗粒详情</h1>
      </header>

      {error ? (
        <p className="rounded-xl border border-[#F2C6CC] bg-[#FFF3F3] px-4 py-3 text-sm text-danger">{error}</p>
      ) : null}
      {loading ? <p className="text-sm text-muted">加载中…</p> : null}
      {!loading && draft ? (
        <div className="space-y-4">
          {embeddedImage ? (
            <section className="rounded-xl border border-border bg-surface p-4 shadow-card">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">嵌入图</p>
              <div className="overflow-hidden rounded-lg border border-border bg-canvas">
                <TtqibEmbeddedImage
                  record={embeddedImage}
                  height={280}
                  zoomable
                  zoomTitle={draft.title || '嵌入图'}
                />
              </div>
            </section>
          ) : null}

          <section className="rounded-xl border border-border bg-surface p-4 shadow-card">
            <h2 className="mb-2 text-sm font-semibold text-ink">隐性能力</h2>
            {implicitCapabilities.length ? (
              <ul className="space-y-2">
                {implicitCapabilities.map((text, index) => (
                  <li
                    key={`${text}-${index}`}
                    className="rounded-lg border border-border bg-canvas/40 px-3 py-2"
                  >
                    <DocumentMathTextBlock text={text} className="text-sm text-ink" />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted">（尚未标注）</p>
            )}
          </section>

          <KnowledgeParticleQaSections
            particle={draftToParticle(draft)}
            headline={draft.title}
            fallbackQuestionText={draft.title}
          />
        </div>
      ) : null}
    </div>
  );
}
